import { Box, Button, IconButton, Typography } from "@mui/material";
import AppBarContainer from "../../components/shared/AppBarContainer";
import PageContentContainer from "../../components/shared/PageContentContainer";
import { useEffect, useState } from "react";
import { BalootUserAPIs } from "../../api/APIs";
import { useRequest } from "../../api/utils/useRequest";
import { useNavigate, useParams } from "react-router-dom";
import BottomFullWidthButtonContainer from "../../components/shared/BottomFullWidthButtonContainer";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { removeNonNumeric, toToman } from "../../utils/Utils";
import HTMLParser from "html-react-parser";
import AmountUnitInputBox from "../../components/shared/AmountUnitInputBox";
import Numeral from "numeral";
import CustomRectangularSkeleton from "../../components/shared/CustomRectangularSkeleton";
import CustomTextSkeleton from "../../components/shared/CustomTextSkeleton";
import { LoadingButton } from "@mui/lab";
import ErrorMessageText from "../../components/shared/ErrorMessageText";
import { ActionModal } from "../../components/shared/ActionModal";

function FinancialAssetSellPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { assetId } = params;

  const [units, setUnits] = useState(0);
  const [amount, setAmount] = useState(0);

  const [errorMessage, setErrorMessage] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const {
    loading,
    result,
    apiCall: getFinancialAssetSellPre,
  } = useRequest(BalootUserAPIs.getFinancialAssetSellPre, true);

  const { loading: otpLoading, apiCall: sendFinancialAssetSellOTP } =
    useRequest(BalootUserAPIs.sendFinancialAssetSellOTP);

  useEffect(() => {
    getFinancialAssetSellPre({ data: { assetId } });
  }, []);

  const onSubmit = () => {
    sendFinancialAssetSellOTP({
      data: { assetId },
      onSuccess: () => {
        navigate(`/app/financial-asset/${assetId}/sell/otp`, {
          state: { units: units },
        });
      },
    });
  };

  return (
    <>
      <AppBarContainer
        rightElement={
          <IconButton
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowForward sx={{ color: "common.white" }} />
          </IconButton>
        }
        centerElement={
          <Typography variant="h5" color={"common.white"}>
            سرمایه‌گذاری
          </Typography>
        }
      />
      <Box
        sx={{
          width: "100%",
          position: "relative",
          zIndex: 1,
          mb: "-50px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: 64,
            height: 64,
            boxShadow: (theme) => theme.shadows[2],
            borderRadius: "14px",
            bgcolor: "common.white",
          }}
        >
          {loading ? (
            <CustomRectangularSkeleton width={42} height={42} />
          ) : (
            result && <img src={result.asset.iconPath} width={42} height={42} />
          )}
        </Box>
      </Box>
      <PageContentContainer
        sx={{
          mt: "20px",
          overflow: "auto",
          pt: 4,
          display: "block",
          pb: `calc(env(safe-area-inset-bottom) + 112px )`,
        }}
      >
        {loading ? (
          <>
            <CustomTextSkeleton sx={{ mt: 1 }} />
            <CustomTextSkeleton sx={{ mt: 1 }} />
            <CustomRectangularSkeleton sx={{ mt: 2 }} height={100} />
            <CustomRectangularSkeleton sx={{ mt: 3 }} height={200} />
          </>
        ) : (
          result && (
            <>
              {[
                {
                  title: "قیمت فروش تقریبی هر واحد (تومان)",
                  value: toToman(result.asset.revokeAmount),
                },
                {
                  title: "واحدهای موجود",
                  value: result.invested.unit,
                },
              ].map((item) => {
                return (
                  <Box
                    key={item.title}
                    sx={{
                      my: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="subtitle1" color="grey.2">
                      {item.title}
                    </Typography>
                    <Typography variant="body1" color="grey.2">
                      {item.value}
                    </Typography>
                  </Box>
                );
              })}

              <AmountUnitInputBox
                error={!!errorMessage}
                sx={{ my: 2 }}
                unitValue={units}
                unitOnChange={(event) => {
                  errorMessage && setErrorMessage("");
                  const newUnits = Numeral(
                    removeNonNumeric(event.target.value) || "0"
                  ).value();
                  if (newUnits.toString().length < 5) {
                    setAmount(newUnits * result.asset.revokeAmount);
                    setUnits(newUnits);
                  }
                }}
                amountValue={toToman(amount)}
                amountOnChange={(event) => {
                  errorMessage && setErrorMessage("");
                  const newAmount = Numeral(
                    removeNonNumeric(event.target.value) || "0"
                  ).value();
                  if (newAmount.toString().length < 10) {
                    setUnits(
                      Math.floor((newAmount * 10) / result.asset.revokeAmount)
                    );
                    setAmount(newAmount * 10);
                  }
                }}
              />
              <ErrorMessageText errorMessage={errorMessage} />
              <Box
                sx={{
                  mt: 1,
                  borderTop: (theme) => `1px dotted ${theme.palette.grey[4]}`,
                }}
              />

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography component={"div"} variant="body2" color="grey.4">
                  <Box display={"inline"}>{units}</Box> واحد{" "}
                  <Box display={"inline"}>
                    {toToman(result.asset.revokeAmount)}
                  </Box>{" "}
                  تومان
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="secondary.main"
                >{`${toToman(
                  units * result.asset.revokeAmount
                )} تومان`}</Typography>
              </Box>

              <Box
                sx={{
                  fontSize: "12px",
                  color: "grey.2",
                  "& h4": { fontSize: "14px" },
                }}
              >
                {HTMLParser(result.asset.characteristics.revokeCondition)}
              </Box>
            </>
          )
        )}
        <BottomFullWidthButtonContainer>
          <LoadingButton
            disabled={loading || !result || units === 0}
            onClick={() => {
              if (units > result.invested.unit) {
                setErrorMessage("تعداد واحد انتخابی بیش‌تر از موجودی است.");
              } else {
                setOpenModal(true);
              }
            }}
            color="error"
            sx={{
              borderRadius: "12px",
              typography: "h3",
            }}
            variant="outlined"
            fullWidth
          >
            تکمیل فرآیند فروش
          </LoadingButton>
        </BottomFullWidthButtonContainer>
      </PageContentContainer>
      {result && (
        <ActionModal
          open={openModal}
          setOpen={setOpenModal}
          title="فروش واحد‌های صندوق"
          description={`آیا از فروش ${units} واحد صندوق ${result.asset.name} اطمینان دارید؟`}
          isContained
          actionTitle="فروش"
          action={onSubmit}
          cancelTitle="بستن"
          loading={otpLoading}
        />
      )}
    </>
  );
}

export default FinancialAssetSellPage;
